/** Context line, the two pickers, the selection chip and the computed-CSS box (spec rows 6 to 10). */
import { Crosshair, SquareDashed, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTabs } from '@/hooks/useTabs';
import type { CaptureResult } from '@/lib/feedback';
import type { PickMode } from './usePickController';

const round = (n: number): number => Math.round(n);

export function chipText(capture: CaptureResult | null): string | null {
  if (!capture?.rect) return null;
  const { width, height, left, top } = capture.rect;
  if (capture.mode === 'element') return `${capture.selector ?? 'element'} · ${round(width)}×${round(height)}`;
  if (capture.mode === 'region') return `Region · ${round(width)}×${round(height)} at ${round(left)}, ${round(top)}`;
  return null;
}

interface Props {
  capture: CaptureResult | null;
  disabled: boolean;
  onPick: (mode: PickMode) => void;
  onClear: () => void;
}

export function SelectionSection({ capture, disabled, onPick, onClear }: Readonly<Props>) {
  const { activeManifest } = useTabs();
  const context = `${activeManifest?.label ?? 'Overview'} · ${globalThis.location.pathname}`;
  const text = chipText(capture);
  const styles = capture?.mode === 'element' ? Object.entries(capture.computedStyles ?? {}) : [];

  return (
    <>
      <p data-testid="feedback-context" title={context} className="truncate text-xs text-muted-foreground">
        {context}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="xs"
          data-testid="feedback-pick-element"
          disabled={disabled}
          onClick={() => onPick('element')}
        >
          <Crosshair aria-hidden />
          Pick element
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          data-testid="feedback-pick-region"
          disabled={disabled}
          onClick={() => onPick('region')}
        >
          <SquareDashed aria-hidden />
          Pick region
        </Button>
      </div>
      <Badge
        variant="outline"
        data-testid="feedback-selection-chip"
        className="h-auto w-full justify-start gap-1 rounded-md py-1"
      >
        {text ? (
          <>
            <span title={text} className="min-w-0 flex-1 truncate font-mono text-xs">
              {text}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Clear selection"
              data-testid="feedback-selection-clear"
              disabled={disabled}
              onClick={onClear}
            >
              <X aria-hidden />
            </Button>
          </>
        ) : (
          <span className="font-sans text-xs text-muted-foreground">Nothing selected (page-level note)</span>
        )}
      </Badge>
      {styles.length > 0 && (
        <ScrollArea
          className="rounded-md border border-border bg-card [&>[data-slot=scroll-area-viewport]]:max-h-32"
          data-testid="feedback-css-box"
        >
          <dl
            aria-label="Computed styles of the selected element"
            className="grid grid-cols-[auto_1fr] gap-x-2 p-2 font-mono text-xs"
          >
            {styles.map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="text-muted-foreground">{key}</dt>
                <dd className="text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </ScrollArea>
      )}
    </>
  );
}
